import { formatDateHyphenated } from 'Shared/utils';
import {
  AddressType,
  ApplicationStatus,
  CommandStatusType,
  CommandType,
  DriverType,
  FieldDataType,
  InsurableItemType,
  InsurablePartyType,
  InsuranceProductType,
  PolicyHolderType,
  ReportResponseType,
  ServerResponseType,
  SoftStopType,
  TelematicsType,
  ThirdPartyReports,
} from './types';
import faker from 'faker';
import { Factory } from 'fishery';
import { DateTime } from 'luxon';

type PolicyHolderTransientParams = {
  driverType?: DriverType;
};

export const policyHolderFactory = Factory.define<
  PolicyHolderType,
  PolicyHolderTransientParams
>(({ sequence, transientParams }) => ({
  identifier: sequence.toString(),
  policyHolderType: {
    value: transientParams.driverType ?? 'principalInsured',
  },
}));

type InsuranceProductTransientParams = {
  additionalInsured?: boolean;
  excludedPerson?: boolean;
  multilineDiscount?: boolean;
  telematicsEnrolled?: boolean;
};

export const insuranceProductFactory = Factory.define<
  InsuranceProductType,
  InsuranceProductTransientParams
>(({ transientParams }) => {
  const policyHolders = [
    policyHolderFactory.build(undefined, {
      transient: { driverType: 'principalInsured' },
    }),
  ];

  if (transientParams.additionalInsured) {
    policyHolders.push(
      policyHolderFactory.build(undefined, {
        transient: {
          driverType: 'additionalInsured',
        },
      })
    );
  }

  if (transientParams.excludedPerson) {
    policyHolders.push(
      policyHolderFactory.build(undefined, {
        transient: {
          driverType: 'excludedPerson',
        },
      })
    );
  }

  return {
    attributes: {
      agencyInformation: {
        attributes: {
          agencyBundle: {
            attributes: {
              carrier: buildFieldData({ value: '' }),
              eligible: buildFieldData({
                value: transientParams.multilineDiscount ? true : false,
              }),
              policyType: buildFieldData({ value: '' }),
            },
          },
        },
      },
      policyTerm: {
        attributes: {
          effectiveDate: buildFieldData({
            value: formatDateHyphenated(
              DateTime.fromJSDate(new Date()).toISODate()
            ),
          }),
          terminationDate: buildFieldData({
            value: formatDateHyphenated(
              DateTime.fromJSDate(new Date()).toISODate()
            ),
          }),
        },
      },
      insurableItemParties: { elements: [], errors: [] },
      policyHolders: policyHolders,
      productType: 'personalAuto',
      productSegment: 'standardAuto',
      coverages: {
        insurableItem: [],
        insuranceProduct: [],
      },
      telematics:
        typeof transientParams.telematicsEnrolled === 'boolean'
          ? telematicsFactory.build({
              enrolled: transientParams.telematicsEnrolled,
            })
          : null,
    },
    commands: [
      {
        name: 'add_driver',
        runnable: true,
        status: 'runnable',
        uri:
          '/api/v3/insurance_applications/863b41a8-3086-42a7-91bb-e8378c913387/add_driver',
      },
      {
        uri:
          '/api/v3/insurance_applications/b94f0574-6676-4504-bba2-4da04b0ae890/add_vehicle',
        name: 'add_vehicle',
        runnable: true,
        status: 'runnable',
      },
    ],
  };
});

type InsurablePartyTransientParams = {
  completed?: boolean;
  additionalInsured?: boolean;
  excludedPerson?: boolean;
  discoveredDriver?: boolean;
};

export const insurablePartyFactory = Factory.define<
  InsurablePartyType,
  InsurablePartyTransientParams
>(({ sequence, transientParams }) => {
  const {
    completed,
    additionalInsured,
    excludedPerson,
    discoveredDriver,
  } = transientParams;
  const principalInsured = !additionalInsured && !excludedPerson;
  return {
    commands: [],
    attributes: {
      identifier: sequence.toString(),
      commands: [],
      editable: true,
      accidents: accidentFactory.buildList(
        transientParams.completed ? 1 : 0,
        undefined,
        { transient: { completed: transientParams.completed } }
      ),
      coverageHistory: {
        attributes: {
          coveredWithinLookbackPeriod: buildFieldData({
            value:
              completed && principalInsured ? 'coveredWithinLast5Years' : null,
          }),
        },
      },
      currentInsurance: {
        attributes: {
          biLevel: buildFieldData({
            value: completed && principalInsured ? 'biLevel-value-1' : null,
          }),
          carrierName: buildFieldData({
            value:
              completed && principalInsured
                ? faker.company.companyName()
                : null,
          }),
          insured: buildFieldData({
            value: completed && principalInsured ? true : null,
          }),
          lapseDuration: buildFieldData({
            value: '',
          }),
          lapseReason: buildFieldData({
            value: '',
          }),
          lapseReasonOther: buildFieldData({
            value: '',
          }),
          insuredDuration: buildFieldData({
            value: '',
          }),
        },
        commands: [],
      },
      discoveredDriver: buildFieldData({ value: !!discoveredDriver }),
      driverLicense: {
        attributes: {
          number: buildFieldData({
            value:
              completed && !excludedPerson
                ? faker.random.alphaNumeric(10)
                : null,
          }),
          state: buildFieldData({
            value: completed && !excludedPerson ? 'state-value-1' : null,
          }),
          status: buildFieldData({
            value: completed && !excludedPerson ? 'active' : null,
          }),
          verified: buildFieldData({
            value: !!completed && !excludedPerson,
          }),
          hasForeignLicense: buildFieldData({
            value: !!completed && !excludedPerson,
          }),
          hasLearnersPermit: buildFieldData({
            value: !!completed && !excludedPerson,
          }),
          userReportedStatus: buildFieldData({
            value: completed && !excludedPerson ? 'active' : null,
          }),
          reportStatus: buildFieldData({
            value: completed && !excludedPerson ? 'valid' : null,
          }),
        },
        commands: [],
      },
      drivingCourse: {
        attributes: {
          completionDate: buildFieldData({
            value: completed && !excludedPerson ? '2008-11-17' : null,
          }),
          courseTaken: buildFieldData({ value: completed ? true : null }),
        },
      },
      drivingHistory: {
        attributes: {
          ageFirstLicensed: buildFieldData({
            value: completed && !excludedPerson ? 18 : null,
          }),
          licensePreviouslySuspended: buildFieldData({
            value: completed && !excludedPerson ? false : null,
          }),
          previousSuspensionCount: buildFieldData({
            value: '',
          }),
          previousSuspensionDate: buildFieldData({
            value: '',
          }),
        },
      },
      education: {
        attributes: {
          currentLevel: buildFieldData({
            value: completed && !excludedPerson ? 'currentLevel-value-1' : null,
          }),
        },
      },
      excludeFromQuote: buildFieldData({
        value: !!excludedPerson,
      }),
      exclusionReason: buildFieldData({
        value: completed && excludedPerson ? 'exclusionReason-value-1' : null,
      }),
      homeOwnership: {
        attributes: {
          homeOwner: buildFieldData({
            value:
              completed && principalInsured ? faker.datatype.boolean() : null,
          }),
        },
      },
      statusDetails: {
        attributes: {
          hasAccessToVehicles: buildFieldData({ value: completed ?? false }),
          hasOwnInsurancePolicy: buildFieldData({ value: completed ?? false }),
          livesInHousehold: buildFieldData({ value: completed ?? false }),
        },
      },
      stateResidency: {
        attributes: {
          inStateResidencyDuration: buildFieldData({
            value: completed ? 'lessThan10Months' : null,
          }),
          multipleHouseholdsMultipleStates: buildFieldData({
            value: completed ?? false,
          }),
          outOfStateStudent: buildFieldData({ value: completed ?? false }),
          ownVehicleWhileOutOfStateStudent: buildFieldData({
            value: completed ?? false,
          }),
        },
      },
      militaryService: {
        attributes: {
          isDeployed: buildFieldData({
            value:
              completed && !excludedPerson ? faker.datatype.boolean() : null,
          }),
          militaryRank: buildFieldData({
            value: completed && !excludedPerson ? 'militaryRank-value-1' : null,
          }),
          serviceType: buildFieldData({
            value:
              completed && !excludedPerson ? 'full_time_active_duty' : null,
          }),
        },
      },
      personalInfo: {
        attributes: {
          dateOfBirth: buildFieldData({
            value: completed ? '1975-04-27' : null,
          }),
          firstName: buildFieldData({
            value: completed ? faker.name.firstName() : null,
          }),
          gender: buildFieldData({
            value: completed && !excludedPerson ? 'gender-value-1' : null,
          }),
          lastName: buildFieldData({
            value: completed ? faker.name.lastName() : null,
          }),
          maritalStatus: buildFieldData({
            value:
              completed && !excludedPerson ? 'maritalStatus-value-1' : null,
          }),
          middleName: buildFieldData({
            value: completed ? faker.name.middleName() : null,
          }),
          relationshipToApplicant: buildFieldData({
            value:
              completed && !principalInsured
                ? 'relationshipToApplicant-value-1'
                : null,
          }),
          ssn: buildFieldData({
            value: completed ? '***-**-****' : null,
          }),
          declinedSsn: buildFieldData({
            value: completed ? false : null,
          }),
          suffix: buildFieldData({
            value: completed ? 'suffix-value-1' : null,
          }),
        },
      },
      contactInformation: {
        attributes: {
          email: buildFieldData({
            value: completed ? faker.internet.email() : null,
          }),
          telephone: {
            attributes: {
              phoneType: buildFieldData({
                value: completed ? 'mobile' : null,
              }),
              number: buildFieldData({
                value: completed ? '5555555555' : null,
              }),
            },
          },
        },
      },
      profession: buildFieldData({
        value: completed && !excludedPerson ? faker.name.jobTitle() : null,
      }),
      isGoodStudent: buildFieldData({
        value: completed ? 'good-student' : null,
      }),
      violations: violationFactory.buildList(
        transientParams.completed ? 1 : 0,
        undefined,
        { transient: { completed: transientParams.completed } }
      ),
      selectiveMvrOrdering: buildFieldData({
        value: 'order',
      }),
      cfr: {
        attributes: {
          required: buildFieldData({
            value: false,
          }),
          caseNumber: buildFieldData({
            value: completed && !excludedPerson ? 'case-number' : null,
          }),
          cfrType: buildFieldData({
            value: completed && !excludedPerson ? 'cfr-type' : null,
          }),
          issueState: buildFieldData({
            value: completed && !excludedPerson ? 'issue-state' : null,
          }),
        },
      },
    },
  };
});

type IncidentTransientParams = {
  completed?: boolean;
};

export const accidentFactory = Factory.define<
  InsurablePartyType['attributes']['accidents'][0],
  IncidentTransientParams
>(({ sequence, transientParams }) => ({
  attributes: {
    identifier: sequence.toString(),
    atFault: buildFieldData({
      value: transientParams.completed ? faker.datatype.boolean() : null,
    }),
    lossType: buildFieldData({
      value: transientParams.completed ? 'lossType-value-1' : null,
    }),
    accidentType: buildFieldData({
      value: transientParams.completed ? 'accidentType-value-1' : null,
    }),
    day: buildFieldData({
      value: transientParams.completed ? 'day-value-1' : null,
    }),
    month: buildFieldData({
      value: transientParams.completed ? 'month-value-1' : null,
    }),
    year: buildFieldData({
      value: transientParams.completed ? 2000 : null,
      errors: [],
    }),
    typeOfLoss: buildFieldData({
      value: '',
      errors: [],
    }),
    isLendingLoss: false,
    date: buildFieldData({
      value: '01/12/2023',
      errors: [],
    }),
    lossAmount: buildFieldData({
      value: '100',
      errors: [],
    }),
    claimStatus: buildFieldData({
      value: 'Chargeable',
      errors: [],
    }),
  },
  commands: [],
}));

export const violationFactory = Factory.define<
  InsurablePartyType['attributes']['violations'][0],
  IncidentTransientParams
>(({ sequence, transientParams }) => ({
  attributes: {
    identifier: sequence.toString(),
    month: buildFieldData({
      value: transientParams.completed ? 'month-value-1' : null,
    }),
    day: buildFieldData({
      value: transientParams.completed ? 'month-day-1' : null,
    }),
    sr22: buildFieldData({
      value: transientParams.completed ? faker.datatype.boolean() : null,
    }),
    violationType: buildFieldData({
      value: transientParams.completed ? 'violationType-value-1' : null,
    }),
    year: buildFieldData({
      value: transientParams.completed ? 2000 : null,
      errors: [],
    }),
  },
  commands: [],
}));

type TelematicsTransientParams = {
  enrolled?: boolean;
};

export const telematicsFactory = Factory.define<
  TelematicsType,
  TelematicsTransientParams
>(({ transientParams }) => ({
  enrolled: transientParams.enrolled ?? false,
}));

type InsurableItemTransientParams = {
  completed?: boolean;
  needsGaragingAddress?: boolean;
};

const getBooleanData = (completed?: boolean) =>
  buildFieldData({ value: completed ? faker.datatype.boolean() : null });
const getStringData = (completed?: boolean) =>
  buildFieldData({ value: completed ? faker.random.word() : null });
const getNumberData = (completed?: boolean) =>
  buildFieldData({ value: completed ? faker.datatype.number() : null });

export const buildFieldData = <T>(data: {
  value: T | null;
  errors?: FieldDataType['errors'];
  source?: string;
  editable?: boolean;
  masked?: boolean;
}): FieldDataType<T> => {
  return {
    value: data.value,
    errors: data.errors || [],
    source: data.source || 'user_reported',
    editable: data.editable || true,
    masked: data.masked || false,
  };
};

export const insurableItemsFactory = Factory.define<
  InsurableItemType,
  InsurableItemTransientParams
>(({ sequence, transientParams }) => {
  const { completed, needsGaragingAddress } = transientParams;

  return {
    attributes: {
      identifier: sequence.toString(),
      editable: true,
      antiTheft: {
        attributes: {
          activeDisable: getBooleanData(completed),
          alarm: getBooleanData(completed),
          passiveDisable: getBooleanData(completed),
          recovery: getBooleanData(completed),
          hasAntiTheftDevice: getBooleanData(completed),
        },
      },
      itemType: 'vehicle',
      mileage: {
        attributes: {
          annual: buildFieldData({ value: 12000 }),
          total: getStringData(completed),
        },
      },
      ownership: {
        attributes: {
          lienholders: [
            {
              attributes: {
                name: getStringData(completed),
                address: {
                  attributes: {
                    addressLine1: getStringData(completed),
                    addressLine2: getStringData(completed),
                    city: getStringData(completed),
                    state: getStringData(completed),
                    postalCode: getStringData(completed),
                  },
                },
              },
            },
          ],
          ownershipType: getStringData(completed),
          registeredToApplicant: getBooleanData(completed),
        },
      },
      parkingDetails: {
        attributes: {
          inStateParkingDuration: getStringData(completed),
          parkingLocationReason: getStringData(completed),
        },
      },
      parkingType: getStringData(completed),
      usageDetails: {
        attributes: {
          usedInMexico: getBooleanData(completed),
          availableForNonCoveredDrivers: getBooleanData(completed),
          emergencyServices: getBooleanData(completed),
          feeBasedServices: getBooleanData(completed),
          hazmatToolTransport: getBooleanData(completed),
          usedAsResidence: getBooleanData(completed),
        },
      },
      rideshareUse: getBooleanData(completed),
      usage: getStringData(completed),
      vehicleIdentification: {
        attributes: {
          vehicleServicesId: getStringData(completed),
          partialVin: getStringData(completed),
          vin: getStringData(completed),
          year: getNumberData(completed),
          make: getStringData(completed),
          model: getStringData(completed),
          bodyStyle: getStringData(completed),
        },
      },
      needsGaragingAddress: !!needsGaragingAddress,
      garagingAddress: {
        attributes: {
          address: {
            attributes: {
              addressLine1: getStringData(completed),
              addressLine2: getStringData(completed),
              city: getStringData(completed),
              postalCode: getStringData(completed),
              state: getStringData(completed),
              addressValidationOutcome: getStringData(completed),
            },
          },
        },
      },
      garagedAtPrimaryAddress: getStringData(completed),
      businessUse: getBooleanData(completed),
      businessUseType: getStringData(completed),
    },
    commands: [
      {
        uri:
          '/api/v3/insurance_applications/40ffeee5-5a11-496d-8f34-a6a7d5fef20b/vehicles/96efedf3-dee3-4405-8b69-a3e1cf182776/delete_vehicle',
        name: 'delete_vehicle',
        runnable: true,
        status: 'runnable',
      },
    ],
  };
});

type CommandTransientParams = {
  name?: string;
  runnable?: boolean;
  status?: CommandStatusType;
};

export const commandFactory = Factory.define<
  {
    name: string;
    runnable: boolean;
    status: CommandStatusType;
    uri: string;
  },
  CommandTransientParams
>(({ transientParams }) => ({
  name: transientParams.name || faker.random.word(),
  runnable: transientParams.runnable || true,
  status: transientParams.status || 'runnable',
  uri: faker.random.word(),
}));

export const reportResponseFactory = Factory.define<
  ReportResponseType,
  CommandTransientParams
>(({ transientParams }) => {
  const transientName = transientParams.name;
  const name =
    Object.values(ThirdPartyReports).find(value => value === transientName) ||
    ThirdPartyReports.All;

  return {
    command: {
      name: 'run_report_' + name,
      runnable: transientParams.runnable || true,
      status: transientParams.status || 'runnable',
      uri: faker.random.word(),
    },
    reportEnum: name,
  };
});

export const softStopFactory = Factory.define<
  SoftStopType,
  { displayed?: boolean }
>(({ transientParams }) => ({
  identifier: faker.datatype.uuid(),
  softStopType: 'tier_5',
  displaySoftStop: transientParams.displayed || true,
}));

type GetDetailsTransientParams = {
  completed?: boolean;
  additionalInsured?: boolean;
  excludedPerson?: boolean;
  discoveredDriver?: boolean;
  status?: ApplicationStatus;
  invalidAddress?: boolean;
  commands?: Array<CommandType>;
  reports?: Array<ReportResponseType>;
  multilineDiscount?: boolean;
  reportsRunnable?: boolean;
  softStops: Array<SoftStopType>;
  quoteChangeAcknowledgement?: boolean;
  telematicsEnrolled?: boolean;
  needsGaragingAddress?: boolean;
  underwritingConsentRequired?: boolean;
  underwritingConsentGivenAt?: string | null;
};

export const getDetailsResponseFactory = Factory.define<
  ServerResponseType,
  GetDetailsTransientParams
>(({ sequence, transientParams }) => {
  const insurableParties = [
    insurablePartyFactory.build(undefined, {
      transient: { completed: transientParams.completed },
    }),
  ];

  const address: AddressType = {
    attributes: {
      identifier: sequence.toString(),
      addressValidationOutcome: buildFieldData({
        errors: [],
        value: transientParams.invalidAddress ? 'no_match' : 'mailable',
      }),
      addressLine1: getStringData(transientParams.completed),
      addressLine2: getStringData(transientParams.completed),
      city: getStringData(transientParams.completed),
      state: getStringData(transientParams.completed),
      postalCode: buildFieldData({
        value: transientParams.completed
          ? faker.address.zipCode('#####')
          : null,
      }),
      addressType: buildFieldData({
        value: 'residency',
      }),
    },
    commands: [
      {
        uri:
          '/api/v3/insurance_applications/4454b850-2b07-4d95-ae2b-0408c1aeb5ee/override_address_outcome',
        name: 'override_address_outcome',
        runnable: !!transientParams.invalidAddress,
        status: transientParams.invalidAddress ? 'runnable' : 'not_runnable',
      },
    ],
  };

  if (transientParams.additionalInsured) {
    insurableParties.push(
      insurablePartyFactory.build(undefined, {
        transient: {
          completed: transientParams.completed,
          additionalInsured: true,
        },
      })
    );
  }

  if (transientParams.excludedPerson) {
    insurableParties.push(
      insurablePartyFactory.build(undefined, {
        transient: {
          completed: transientParams.completed,
          excludedPerson: true,
        },
      })
    );
  }

  if (transientParams.discoveredDriver) {
    insurableParties.push(
      insurablePartyFactory.build(undefined, {
        transient: {
          completed: transientParams.completed,
          additionalInsured: true,
          discoveredDriver: true,
        },
      })
    );
  }

  return {
    insuranceApplication: {
      attributes: {
        quoteNumber: 'Q0000-0025-9915',
        identifier: sequence.toString(),
        insurableParties: insurableParties,
        insurableItemParties: [],
        insuranceProducts: insuranceProductFactory.buildList(1, undefined, {
          transient: {
            additionalInsured: transientParams.additionalInsured,
            excludedPerson: transientParams.excludedPerson,
            multilineDiscount: transientParams.multilineDiscount,
            telematicsEnrolled: transientParams.telematicsEnrolled,
          },
        }),
        insurableItems: insurableItemsFactory.buildList(1, undefined, {
          transient: {
            completed: transientParams.completed,
            needsGaragingAddress: transientParams.needsGaragingAddress,
          },
        }),
        addresses: [address],
        underwriting: {},
        underwritingConsent: {
          required: transientParams.underwritingConsentRequired || false,
          givenAt: transientParams.underwritingConsentGivenAt || null,
        },
        reports: transientParams.reports || [],
        status: transientParams.status ?? 'open',
        documentSignatures: [],
        softStops: transientParams.softStops ? transientParams.softStops : [],
        disclosures: [],
        quoteChangeAcknowledgement: !!transientParams.quoteChangeAcknowledgement,
        paymentPreferences: {
          frequency: 'in_full',
          method: 'credit_card',
          paymentPlanEnum: 'in_full',
          variableFeeStrategySelected: false,
        },
        flexiblePaymentPreferences: {
          frequency: 'monthly',
          daysOfMonth: [],
          daysOfWeek: [],
        },
        configDiscoveryParams: 'some base64-encoded string',
      },
      commands: transientParams.commands ? transientParams.commands : [],
    },
  };
});

export const multipleCompletedDriversDetailsResponseFactory = getDetailsResponseFactory.transient(
  { completed: true, additionalInsured: true, excludedPerson: true }
);

export const completedDriverDetailsResponseFactory = getDetailsResponseFactory.transient(
  { completed: true }
);

export const driverWithAddressDetailsResponseFactory = getDetailsResponseFactory.transient(
  { completed: true }
);

export const driverWithInvalidAddressDetailsResponseFactory = getDetailsResponseFactory.transient(
  { invalidAddress: true }
);

export const completedVehicleDetailsResponseFactory = getDetailsResponseFactory.transient(
  { completed: true }
);

export const bindableDetailsResponseFactory = getDetailsResponseFactory.transient(
  {
    completed: true,
    commands: [
      commandFactory.build({
        name: 'bind',
        runnable: true,
        status: 'runnable',
      }),
    ],
  }
);

export const ncfRunnableDetailsResponseFactory = getDetailsResponseFactory.transient(
  {
    completed: true,
    commands: [
      commandFactory.build({
        name: 'bind',
        runnable: false,
        status: 'not_runnable',
      }),
    ],
    reports: [
      reportResponseFactory
        .transient({
          name: 'all',
          runnable: false,
          status: 'not_runnable',
        })
        .build(),
      reportResponseFactory
        .transient({
          name: 'ncf_and_current_carrier',
          runnable: false,
          status: 'not_runnable',
        })
        .build(),
      reportResponseFactory
        .transient({
          name: 'driver_data_enhancement',
          runnable: true,
          status: 'runnable',
        })
        .build(),
      reportResponseFactory
        .transient({
          name: 'ncf',
          runnable: true,
          status: 'runnable',
        })
        .build(),
      reportResponseFactory
        .transient({
          name: 'clue',
          runnable: true,
          status: 'runnable',
        })
        .build(),
    ],
  }
);

export const boundDetailsResponseFactory = getDetailsResponseFactory.transient({
  status: 'bound',
  completed: true,
});

export const multiLineDiscountDetailsResponseFactory = getDetailsResponseFactory.transient(
  {
    completed: true,
    multilineDiscount: true,
  }
);

export const telematicsEnrollmentResponseFactory = getDetailsResponseFactory.transient(
  {
    telematicsEnrolled: undefined,
  }
);

export const getDeadDetailsResponseFactory = getDetailsResponseFactory.transient(
  { status: 'dead', completed: true }
);

export const getStoppedDetailsResponseFactory = getDetailsResponseFactory.transient(
  { status: 'stop', completed: true }
);

export const getMoratoriumDetailsResponseFactory = getDetailsResponseFactory.transient(
  { status: 'in_moratorium', completed: true }
);

export const getOutOfMarketDetailsResponseFactory = getDetailsResponseFactory.transient(
  { status: 'out_of_market', completed: true }
);

export const getTPRInProgressDetailsResponseFactory = getDetailsResponseFactory.transient(
  { status: 'third_party_in_progress', completed: true }
);

export const getBindInProgressDetailsResponseFactory = getDetailsResponseFactory.transient(
  { status: 'bind_in_progress', completed: true }
);

export const getDisplayedSoftStopDetailsResponse = getDetailsResponseFactory.transient(
  {
    completed: true,
    softStops: [softStopFactory.build()],
  }
);

export const getUndisplayedSoftStopDetailsResponse = getDetailsResponseFactory.transient(
  {
    completed: true,
    softStops: [softStopFactory.build({ displaySoftStop: false })],
  }
);

export const getUndisclosedDriversDetailsResponse = getDetailsResponseFactory.transient(
  { completed: true, discoveredDriver: true }
);

export const getStoppedWithUndisclosedDriversDetailsResponse = getDetailsResponseFactory.transient(
  { completed: true, discoveredDriver: true, status: 'stop' }
);
